import { useCallback, useEffect, useRef } from "react";

type GetUserMedia = (
  constraints?: MediaStreamConstraints,
) => Promise<MediaStream>;

/**
 *
 * FaceLivenessDetector requests the camera on its own (and can do it more than
 * once per session), so reading the <video> srcObject is not reliable: by the
 * time the detector unmounts the element is already out of the DOM and its
 * stream stays open. Instead we wrap getUserMedia for the whole lifetime of the
 * consumer component to keep track of every stream, and stop them as soon as
 * `active` turns false or the component unmounts.
 */
const useCameraRelease = (active: boolean) => {
  const streamsRef = useRef<Set<MediaStream>>(new Set());

  const stopStreams = useCallback(() => {
    streamsRef.current.forEach((stream) =>
      stream.getTracks().forEach((track) => track.stop()),
    );
    streamsRef.current.clear();
  }, []);

  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia) return;

    const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);

    const trackedGetUserMedia: GetUserMedia = async (constraints) => {
      const stream = await originalGetUserMedia(constraints);
      streamsRef.current.add(stream);
      return stream;
    };

    mediaDevices.getUserMedia = trackedGetUserMedia;

    return () => {
      // Only restore if nobody else wrapped it afterwards
      if (mediaDevices.getUserMedia === trackedGetUserMedia)
        mediaDevices.getUserMedia = originalGetUserMedia;
      stopStreams();
    };
  }, [stopStreams]);

  useEffect(() => {
    if (!active) stopStreams();
  }, [active, stopStreams]);
};

export default useCameraRelease;